'use client'

import {
  MessageScroller as MessageScrollerPrimitive,
  useMessageScroller,
  useMessageScrollerScrollable,
  useMessageScrollerVisibility,
} from '@shadcn/react/message-scroller'
import { cn } from 'lib/utils'
import { ArrowDownIcon } from 'lucide-react'
import { Button } from 'primitives/button'
import type * as React from 'react'

/**
 * Vendored from shadcn/ui `message-scroller` (new-york-v4, radix flavor),
 * adapted for Neo: Tailwind v3.4 syntax (arbitrary properties for
 * scrollbar/containment, no v4 `scroll-fade-b`/`data-autoscrolling:`
 * utilities, logical `inset-s-*` → `left-*`), Neo Button sizes (no icon-sm).
 * Chat scroll container for streaming conversations: turn anchoring,
 * live-edge auto-follow, history preservation, `content-visibility` row
 * skipping. Behavior lives in `@shadcn/react`.
 */

function MessageScrollerProvider(
  props: React.ComponentProps<typeof MessageScrollerPrimitive.Provider>,
) {
  return <MessageScrollerPrimitive.Provider {...props} />
}

function MessageScroller({
  className,
  ...props
}: React.ComponentProps<typeof MessageScrollerPrimitive.Root>) {
  return (
    <MessageScrollerPrimitive.Root
      data-slot="message-scroller"
      className={cn(
        'group/message-scroller relative flex size-full min-h-0 flex-col overflow-hidden',
        className,
      )}
      {...props}
    />
  )
}

function MessageScrollerViewport({
  className,
  ...props
}: React.ComponentProps<typeof MessageScrollerPrimitive.Viewport>) {
  return (
    <MessageScrollerPrimitive.Viewport
      data-slot="message-scroller-viewport"
      className={cn(
        // overflow-anchor:none — the scroller drops auto-follow on any >0.5px
        // upward scrollTop delta it did not cause itself, and browser scroll
        // anchoring produces exactly that when content-visibility rows swap
        // their estimated height for a real one mid-stream. Prepend restore is
        // the scroller's own job (preserveScrollOnPrepend), not anchoring's.
        'size-full min-h-0 min-w-0 overflow-y-auto overscroll-contain [contain:content] [overflow-anchor:none] [scrollbar-width:thin]',
        className,
      )}
      {...props}
    />
  )
}

function MessageScrollerContent({
  className,
  ...props
}: React.ComponentProps<typeof MessageScrollerPrimitive.Content>) {
  return (
    <MessageScrollerPrimitive.Content
      data-slot="message-scroller-content"
      className={cn('flex h-max min-h-full flex-col gap-8', className)}
      {...props}
    />
  )
}

function MessageScrollerItem({
  className,
  scrollAnchor = false,
  ...props
}: React.ComponentProps<typeof MessageScrollerPrimitive.Item>) {
  return (
    <MessageScrollerPrimitive.Item
      data-slot="message-scroller-item"
      scrollAnchor={scrollAnchor}
      className={cn(
        'min-w-0 shrink-0 [contain-intrinsic-size:auto_10rem] [content-visibility:auto]',
        className,
      )}
      {...props}
    />
  )
}

function MessageScrollerButton({
  direction = 'end',
  className,
  children,
  render,
  ...props
}: React.ComponentProps<typeof MessageScrollerPrimitive.Button>) {
  return (
    <MessageScrollerPrimitive.Button
      data-slot="message-scroller-button"
      data-direction={direction}
      direction={direction}
      className={cn(
        'absolute left-1/2 -translate-x-1/2 rounded-full border border-st1 bg-background/70 text-secondary shadow-bs1 backdrop-blur-md transition-[transform,opacity] duration-200 hover:bg-background/90 hover:text-foreground data-[active=false]:pointer-events-none data-[active=false]:scale-95 data-[active=false]:opacity-0 data-[active=false]:duration-150 data-[active=false]:ease-[cubic-bezier(0.7,0,0.84,0)] data-[active=true]:translate-y-0 data-[active=true]:scale-100 data-[active=true]:opacity-100 data-[active=true]:ease-[cubic-bezier(0.23,1,0.32,1)] data-[direction=end]:bottom-3 data-[direction=end]:data-[active=false]:translate-y-full data-[direction=start]:top-3 data-[direction=start]:data-[active=false]:-translate-y-full data-[direction=start]:[&_svg]:rotate-180',
        className,
      )}
      render={render ?? <Button variant="ghost" size="xs" className="rounded-full" />}
      {...props}
    >
      {children ?? (
        <>
          <ArrowDownIcon className="h-3.5 w-3.5" />
          <span className="sr-only">
            {direction === 'end' ? 'Scroll to end' : 'Scroll to start'}
          </span>
        </>
      )}
    </MessageScrollerPrimitive.Button>
  )
}

export {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
  useMessageScroller,
  useMessageScrollerScrollable,
  useMessageScrollerVisibility,
}
